/**
 * AssessmentPanel — right-hand pane of the department-assessment screen.
 *
 * Filter by department, search the document, and hold a per-fragment discussion
 * thread (read existing messages + post a new one). Threads are backed by the
 * generic Discussion API via discussionsSlice; posting sends immediately (no
 * global save step).
 */

import React, { useMemo, useState } from "react";
import {
    alpha,
    Box,
    Divider,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { Localized } from "@fluent/react";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { selectLoggedUser } from "../../../user/userSlice";
import { CompanyDepartmentDetails } from "../../../administration/companyDepartments/companyDepartmentsSlice";
import {
    deleteDiscussionMessage,
    editDiscussionMessage,
    postDiscussionMessage,
    selectMessagesForBlock,
    selectRootMessageForBlock,
    DiscussionMessage,
} from "../discussionsSlice";

interface Props {
    /** Document GUID — the discussion's relatedObjectId. */
    relatedObjectId: string;
    selectedKey: string | null;
    departments: CompanyDepartmentDetails[];
    /** blockKey → assigned department id, for the selected fragment's badge. */
    deptIdByKey: Record<string, number>;
    /** Selected department ids; empty = all departments. */
    selectedDeptIds: number[];
    onSelectedDeptIdsChange: (ids: number[]) => void;
    search: string;
    onSearchChange: (value: string) => void;
}

export const AssessmentPanel: React.FC<Props> = ({
    relatedObjectId,
    selectedKey,
    departments,
    deptIdByKey,
    selectedDeptIds,
    onSelectedDeptIdsChange,
    search,
    onSearchChange,
}) => {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <TextField
                    fullWidth
                    size="small"
                    label={<Localized id="assess-search"><span>_Search</span></Localized>}
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />

                <SectionLabel id="assess-section-filters" fallback="_Filters" />

                <Paper variant="outlined" sx={{ p: 0.75, borderRadius: 2, display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                    <Tooltip title={<Localized id="assess-filter-by-department"><span>_Filter by department</span></Localized>}>
                        <FilterListIcon fontSize="small" />
                    </Tooltip>
                    <ToggleButtonGroup
                        size="small"
                        value={selectedDeptIds}
                        onChange={(_, next) => onSelectedDeptIdsChange((next as number[]) ?? [])}
                        sx={{
                            flexWrap: "wrap",
                            gap: 0.5,
                            // Detach the segments into standalone pills: kill the
                            // group's border-collapsing (negative margin + shared
                            // radius) so each chip can carry its own dept color.
                            "& .MuiToggleButtonGroup-grouped": {
                                m: 0,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1.5,
                            },
                        }}
                    >
                        {departments.map((d) => {
                            // Each chip wears its department's color: a soft tint +
                            // color dot when off, a solid fill when selected (the same
                            // color language as the dept badges in the document).
                            const color = d.color || "#1976d2";
                            return (
                                <ToggleButton
                                    key={d.id}
                                    value={d.id}
                                    sx={{
                                        gap: 0.625,
                                        px: 1, py: 0.25,
                                        textTransform: "none",
                                        fontWeight: 700, fontSize: 12, lineHeight: 1.6,
                                        color: "text.primary",
                                        backgroundColor: alpha(color, 0.12),
                                        "&:hover": { backgroundColor: alpha(color, 0.22) },
                                        "&.Mui-selected": {
                                            color: "#fff",
                                            backgroundColor: color,
                                            "&:hover": { backgroundColor: color },
                                        },
                                        "&.Mui-selected .dept-dot": { backgroundColor: "#fff" },
                                    }}
                                >
                                    <Box
                                        className="dept-dot"
                                        sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: color, flexShrink: 0 }}
                                    />
                                    {d.code}
                                </ToggleButton>
                            );
                        })}
                    </ToggleButtonGroup>
                </Paper>
            </Box>

            {selectedKey ? (
                <DiscussionThread
                    relatedObjectId={relatedObjectId}
                    blockKey={selectedKey}
                    departments={departments}
                    deptIdByKey={deptIdByKey}
                />
            ) : (
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                        <Localized id="assess-panel-title"><span>_Assessment</span></Localized>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        <Localized id="assess-empty-hint">
                            <span>_Choose a fragment from the left to add a comment</span>
                        </Localized>
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

const DiscussionThread: React.FC<{
    relatedObjectId: string;
    blockKey: string;
    departments: CompanyDepartmentDetails[];
    deptIdByKey: Record<string, number>;
}> = ({ relatedObjectId, blockKey, departments, deptIdByKey }) => {
    const dispatch = useAppDispatch();
    const messages = useAppSelector((s) => selectMessagesForBlock(s, blockKey));
    const rootMessage = useAppSelector((s) => selectRootMessageForBlock(s, blockKey));
    const currentUser = useAppSelector(selectLoggedUser);
    const dept = departments.find((d) => d.id === deptIdByKey[blockKey]);

    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);

    // Any selected fragment can be assessed — a department stamp is no longer
    // required. Assessors comment on every line, not only the ones marked for
    // their department; the stamp (when present) is shown for context above.
    const isReply = !!rootMessage;

    // Root first, then replies oldest→newest, so the thread reads top-down.
    const orderedMessages = useMemo(() => {
        const replies = messages
            .filter((m) => m.parentMessageId !== null)
            .slice()
            .sort((a, b) => a.createdDate.localeCompare(b.createdDate));
        return rootMessage ? [rootMessage, ...replies] : messages.slice();
    }, [messages, rootMessage]);

    const send = async () => {
        const content = draft.trim();
        if (!content || sending) return;
        setSending(true);
        try {
            // One thread per fragment (assess-screen rule): the first post starts
            // the thread; every later post replies to its root instead of opening
            // a new one.
            await dispatch(postDiscussionMessage({
                relatedObjectId,
                blockKey,
                content,
                parentMessageId: rootMessage?.id ?? null,
            })).unwrap();
            setDraft("");
        } finally {
            setSending(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {dept && (
                <Box>
                    <SectionLabel id="assess-section-department" fallback="_Department" />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                            sx={{
                                width: 28, height: 28, borderRadius: 0.75,
                                bgcolor: dept.color || "primary.main", color: "#fff",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 10, fontWeight: 700,
                            }}
                        >
                            {dept.code}
                        </Box>
                        <Typography variant="body2" fontWeight={600}>{dept.name}</Typography>
                    </Box>
                </Box>
            )}

            <Box>
                <SectionLabel id="assess-section-comments" fallback="_Comments" />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {messages.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            <Localized id="assess-no-messages"><span>_No comments yet</span></Localized>
                        </Typography>
                    ) : (
                        orderedMessages.map((m) => (
                            <MessageItem
                                key={m.id}
                                message={m}
                                blockKey={blockKey}
                                isReply={m.parentMessageId !== null}
                                ownedByCurrentUser={m.createdBy === currentUser}
                            />
                        ))
                    )}
                </Box>
            </Box>

            <Box>
                <SectionLabel
                    id={isReply ? "assess-section-reply" : "assess-section-new-comment"}
                    fallback={isReply ? "_Reply" : "_New comment"}
                />
                {isReply && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        <Localized id="assess-reply-hint">
                            <span>_This fragment already has a comment — your message will be added as a reply.</span>
                        </Localized>
                    </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
                    <TextField
                        sx={{ backgroundColor: "#fff", flex: 1 }}
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                        inputProps={{ maxLength: 2000 }}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                                e.preventDefault();
                                send();
                            }
                        }}
                        label={
                            isReply
                                ? <Localized id="assess-write-reply"><span>_Write a reply</span></Localized>
                                : <Localized id="assess-write-message"><span>_Write a comment</span></Localized>
                        }
                    />
                    <Tooltip title={<Localized id="assess-send"><span>_Send</span></Localized>}>
                        <span>
                            <IconButton color="primary" disabled={!draft.trim() || sending} onClick={send}>
                                <SendIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    );
};

// NotePanel-style section heading: a centered uppercase caption on a divider.
const SectionLabel: React.FC<{ id: string; fallback: string }> = ({ id, fallback }) => (
    <Divider sx={{ mb: 1 }}>
        <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            letterSpacing={0.5}
            sx={{ textTransform: "uppercase" }}
        >
            <Localized id={id}><span>{fallback}</span></Localized>
        </Typography>
    </Divider>
);

const MessageItem: React.FC<{
    message: DiscussionMessage;
    blockKey: string;
    isReply: boolean;
    ownedByCurrentUser: boolean;
}> = ({ message, blockKey, isReply, ownedByCurrentUser }) => {
    const dispatch = useAppDispatch();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(message.content);
    const [saving, setSaving] = useState(false);

    const startEdit = () => {
        setDraft(message.content);
        setEditing(true);
    };

    const cancel = () => {
        setEditing(false);
        setDraft(message.content);
    };

    const save = async () => {
        const content = draft.trim();
        if (!content || saving) return;
        if (content === message.content) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            await dispatch(editDiscussionMessage({ messageId: message.id, blockKey, content })).unwrap();
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    const remove = () => {
        dispatch(deleteDiscussionMessage({ messageId: message.id, blockKey }));
    };

    return (
        <Box
            sx={
                isReply
                    ? {
                        // Reply: indented, lighter, no accent — clearly subordinate.
                        ml: 3, px: 1, py: 0.75, borderRadius: 1,
                        border: "1px solid var(--blue-200)", bgcolor: "var(--blue-50)",
                    }
                    : {
                        // Root (thread starter): accent left border + stronger fill.
                        px: 1, py: 0.75, borderRadius: 1,
                        border: "1px solid var(--blue-300)", borderLeft: "3px solid var(--blue-600)",
                        bgcolor: "var(--blue-100)",
                    }
            }
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                    {isReply && (
                        <SubdirectoryArrowRightIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                    )}
                    <Typography variant="caption" fontWeight={700} color="var(--blue-800)">
                        {message.createdByFullName || message.createdBy}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                        {new Date(message.createdDate).toLocaleString()}
                    </Typography>
                    {ownedByCurrentUser && !editing && (
                        <>
                            <IconButton size="small" onClick={startEdit} sx={{ p: 0.25 }}>
                                <EditOutlinedIcon fontSize="inherit" />
                            </IconButton>
                            <IconButton size="small" onClick={remove} sx={{ p: 0.25 }}>
                                <DeleteOutlineIcon fontSize="inherit" />
                            </IconButton>
                        </>
                    )}
                </Box>
            </Box>

            {editing ? (
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5, mt: 0.5 }}>
                    <TextField
                        sx={{ backgroundColor: "#fff", flex: 1 }}
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                        autoFocus
                        inputProps={{ maxLength: 2000 }}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                                e.preventDefault();
                                save();
                            } else if (e.key === "Escape") {
                                e.preventDefault();
                                cancel();
                            }
                        }}
                    />
                    <Tooltip title={<Localized id="save"><span>_Save</span></Localized>}>
                        <span>
                            <IconButton color="primary" size="small" disabled={!draft.trim() || saving} onClick={save}>
                                <CheckIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={<Localized id="cancel"><span>_Cancel</span></Localized>}>
                        <IconButton size="small" onClick={cancel}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ) : (
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {message.content}
                </Typography>
            )}
        </Box>
    );
};

export default AssessmentPanel;
